import { Loader2, Radio, Trash2 } from "lucide-react";
import { type SensorTag } from "../../lib/instrumentationApi";
import { InlineSensorReadout } from "./InlineSensorReadout";
import { type AssetOption } from "./SearchableAssetPicker";

interface SensorTagsListProps {
  tags: SensorTag[];
  selectedTagId: string | null;
  onSelect: (tag: SensorTag) => void;
  onDelete: (tag: SensorTag) => void;
  deletingTagId?: string | null;
  /** Site-scoped assets for the searchable asset picker rendered inside
   *  the selected row. Omit to hide the picker (e.g. when the parent
   *  hasn't loaded the asset list). */
  assets?: AssetOption[];
  /** Forwarded to InlineSensorReadout so a successful re-link refreshes
   *  the tag list's denormalized asset name on the row above. */
  onAssetChanged?: () => void;
  /** Navigation callback for the per-row "Open in Instrumentation" link.
   *  Parent supplies the navigate() call so the component stays
   *  router-agnostic. Receives the sensor id. */
  onOpenInInstrumentation?: (sensorId: string) => void;
}

/** Sensor tags sidebar list — used by both 3D viewers. Selecting a row
 *  expands it inline to show the live reading + chart + (optionally)
 *  an asset picker. Replaces the previous floating data panel — the
 *  user explicitly asked for no floating window. */
export function SensorTagsList({
  tags,
  selectedTagId,
  onSelect,
  onDelete,
  deletingTagId,
  assets,
  onAssetChanged,
  onOpenInInstrumentation,
}: SensorTagsListProps) {
  if (tags.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-xs text-surface-500">
        No sensor tags yet. Switch to the Sensor Tag tool and click on the model.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {tags.map((t) => {
        const isSelected = selectedTagId === t.id;
        const isDeleting = deletingTagId === t.id;
        const headline = t.label || t.sensor_name || "Sensor";
        const g = (t.geometry || {}) as { x?: number; y?: number; z?: number };
        const accent = t.color || "#22d3ee";
        return (
          <div
            key={t.id}
            className={
              "group rounded-md border transition-colors " +
              (isSelected
                ? "border-cyan-500/50 bg-cyan-500/10"
                : "border-surface-700/60 bg-surface-900/60 hover:border-cyan-500/40 hover:bg-cyan-500/5")
            }
          >
            <div className="flex items-center gap-2 px-2 py-1.5">
              <button
                onClick={() => onSelect(t)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                title={
                  isSelected
                    ? "Collapse this sensor"
                    : "Expand to see live readings and zoom to this tag in 3D"
                }
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/20"
                  style={{ backgroundColor: accent }}
                />
                <span
                  className={
                    "min-w-0 flex-1 text-xs font-medium " +
                    (isSelected ? "text-cyan-100" : "text-surface-100") +
                    (isSelected ? " break-words" : " truncate")
                  }
                >
                  {headline}
                </span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(t);
                }}
                disabled={isDeleting}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300 disabled:opacity-50"
                title="Delete this sensor 3D tag"
              >
                {isDeleting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
            {isSelected && (
              <div className="space-y-2 border-t border-cyan-500/20 px-3 py-2 text-[11px]">
                {/* Identifying fields — keep these tight so the live readout
                    has room to breathe below. */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                      Sensor
                    </div>
                    <div className="flex items-center gap-1.5 text-surface-100">
                      <Radio className="h-3 w-3 text-cyan-300" />
                      <span className="truncate">{t.sensor_name || "—"}</span>
                      {t.sensor_unit && (
                        <span className="text-[10px] text-surface-400">
                          ({t.sensor_unit})
                        </span>
                      )}
                    </div>
                  </div>
                  {t.device_name && (
                    <div>
                      <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                        Device
                      </div>
                      <div className="truncate text-surface-200">{t.device_name}</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                    Position
                  </div>
                  <div className="font-mono text-[10px] text-surface-400">
                    x: {g.x?.toFixed(3)} · y: {g.y?.toFixed(3)} · z: {g.z?.toFixed(3)}
                  </div>
                </div>
                {/* Live data + asset picker + open-in-instrumentation. The
                    readout component manages its own polling lifecycle. */}
                <InlineSensorReadout
                  sensorId={t.sensor_id}
                  initialDeviceId={t.device_id}
                  accent={accent}
                  assets={assets}
                  onAssetChanged={onAssetChanged}
                  onOpenInInstrumentation={
                    onOpenInInstrumentation
                      ? () => onOpenInInstrumentation(t.sensor_id)
                      : undefined
                  }
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
